import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useCommands, useKeyHint, useShortcutContext, type CommandSpec } from '../../../lib/shortcuts/index.js';
import { filesOfKind, useProjectFiles } from '../../../state/project-files.js';
import { useModel } from '../../../state/model.js';
import { BLEND_MODES, FALLOFFS, PAINT_TOOLS, usePaint, type PaintToolName } from '../../../state/paint.js';
import { NumberField, SelectField, Toggle } from '../../ui/controls.js';
import { Icon, type IconName } from '../../ui/icons.js';
import { BakePanel } from './BakePanel.js';
import '../mesh-edit.css';
import './paint.css';

function toHex(c: readonly number[]): string {
  return `#${c.map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('')}`;
}

function fromHex(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

/** Last pointer position (for F / Shift+F radial adjust). */
let pointerX = 0;
if (typeof window !== 'undefined') window.addEventListener('pointermove', (e) => (pointerX = e.clientX), { passive: true });

/** Texture paint keymap (Blender-like), context `model.paint`. */
function paintCommands(): CommandSpec[] {
  const p = () => usePaint.getState();
  const cmd = (id: string, label: string, keys: string[], run: () => void, extra: Partial<CommandSpec> = {}): CommandSpec => ({
    id: `model.paint.${id}`,
    label,
    category: 'Model · Texture Paint',
    context: 'model.paint',
    keys,
    run,
    ...extra,
  });
  const tool = (t: PaintToolName) => () => p().setBrush({ tool: t });
  const resize = (k: number) => () => p().setBrush({ radius: Math.min(600, Math.max(2, p().brush.radius * k)) });
  return [
    cmd('tool.draw', 'Draw tool', ['1', 'D'], tool('draw')),
    cmd('tool.erase', 'Erase tool', ['2', 'E'], tool('erase')),
    cmd('tool.blur', 'Soften tool', ['3'], tool('blur')),
    cmd('tool.smear', 'Smear tool', ['4'], tool('smear')),
    cmd('tool.clone', 'Clone tool', ['5'], tool('clone')),
    cmd('tool.fill', 'Fill tool', ['6'], tool('fill')),
    cmd('tool.mask', 'Mask tool', ['7', 'M'], tool('mask')),
    cmd('radius', 'Adjust brush radius', ['F'], () => p().startRadial('radius', pointerX)),
    cmd('strength', 'Adjust brush strength', ['Shift+F'], () => p().startRadial('strength', pointerX)),
    cmd('radius.down', 'Smaller brush', ['['], resize(1 / 1.15), { repeat: true }),
    cmd('radius.up', 'Larger brush', [']'], resize(1.15), { repeat: true }),
    cmd('swap', 'Swap colors', ['X'], () => p().swapColors()),
    cmd('sample', 'Sample color under the cursor', ['S'], () => p().sample()),
    cmd('mask.invert', 'Invert mask', ['Ctrl+I'], () => p().mask('invert')),
    cmd('mask.clear', 'Clear mask', ['Alt+M'], () => p().mask('clear')),
    cmd('save', 'Save painted texture', ['Ctrl+S'], () => p().save(), { icon: 'check' }),
    cmd('exit', 'Leave Texture Paint', [], () => p().end(), { icon: 'x' }),
    // Keep Delete from removing the painted part (the Model tab's own key).
    cmd('keep-part', 'Delete (ignored while painting)', ['Delete', 'Backspace'], () => {}, { hidden: true }),
  ];
}

function Popover({ label, title, active, children, wide }: { label: ReactNode; title: string; active?: boolean; children: ReactNode; wide?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [open]);
  return (
    <div className="paint-pop" ref={ref}>
      <button type="button" className={`tb-btn${active ? ' active' : ''}`} title={title} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label} <Icon name="chevronDown" size={11} />
      </button>
      {open && <div className={`paint-pop-body${wide ? ' is-wide' : ''}`}>{children}</div>}
    </div>
  );
}

function Row({ label, children, title }: { label: string; children: ReactNode; title?: string }) {
  return (
    <label className="paint-row" title={title}>
      <span>{label}</span>
      {children}
    </label>
  );
}

/** "Texture Paint" button next to Sculpt in the model toolbar. */
export function PaintModeButton() {
  const active = usePaint((s) => s.active);
  const selected = useModel((s) => s.selected);
  if (active || !selected) return null;
  return (
    <button
      type="button"
      className="tb-btn"
      title="Paint this part's material texture in the viewport (uses its UVs)"
      onClick={() => usePaint.getState().begin(selected)}
    >
      <Icon name="image" size={13} /> Texture Paint
    </button>
  );
}

/** Interactive radius / strength drag after F / Shift+F (click confirms, Esc / right click cancels). */
function RadialControl() {
  const radial = usePaint((s) => s.radial);
  const brush = usePaint((s) => s.brush);
  useEffect(() => {
    if (!radial) return;
    const value = (x: number) =>
      radial.kind === 'radius'
        ? Math.min(600, Math.max(2, radial.start + (x - radial.startX)))
        : Math.min(1, Math.max(0, radial.start + (x - radial.startX) / 300));
    const apply = (x: number) => {
      const v = value(x);
      usePaint.setState((s) => ({ brush: radial.kind === 'radius' ? { ...s.brush, radius: v } : { ...s.brush, strength: v } }));
    };
    const finish = (commit: boolean, x: number) => {
      usePaint.setState({ radial: null });
      const v = commit ? value(x) : radial.start;
      usePaint.getState().setBrush(radial.kind === 'radius' ? { radius: v } : { strength: v });
    };
    const onMove = (e: PointerEvent) => apply(e.clientX);
    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      finish(e.button === 0, e.clientX);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        e.preventDefault();
        e.stopImmediatePropagation();
        finish(e.key === 'Enter', pointerX);
      }
    };
    window.addEventListener('pointermove', onMove, true);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointermove', onMove, true);
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [radial]);
  if (!radial) return null;
  return (
    <span className="tb-btn paint-radial" role="status">
      {radial.kind === 'radius' ? `Radius ${Math.round(brush.radius)} px` : `Strength ${brush.strength.toFixed(2)}`} · move, click to confirm, Esc to cancel
    </span>
  );
}

function StencilOptions() {
  const stencil = usePaint((s) => s.stencil);
  const entries = useProjectFiles((s) => s.entries);
  const images = filesOfKind(entries, 'texture').filter((p) => /\.(png|jpe?g)$/i.test(p));
  const p = usePaint.getState();
  return (
    <>
      <Row label="Image" title="Image projected from the screen onto the surface: scales the brush (white paints, black protects)">
        <SelectField
          value={stencil?.image ?? ''}
          options={[{ value: '', label: 'None' }, ...images.map((i) => ({ value: i, label: i }))]}
          onChange={(v) => p.setStencil(v ? { image: v } : null)}
        />
      </Row>
      {stencil && (
        <>
          <Row label="Center X">
            <NumberField value={stencil.center[0]} step={1} onChange={(v) => p.setStencil({ image: stencil.image, center: [v, stencil.center[1]] })} />
          </Row>
          <Row label="Center Y">
            <NumberField value={stencil.center[1]} step={1} onChange={(v) => p.setStencil({ image: stencil.image, center: [stencil.center[0], v] })} />
          </Row>
          <Row label="Width">
            <NumberField value={stencil.size[0]} step={1} min={1} onChange={(v) => p.setStencil({ image: stencil.image, size: [v, stencil.size[1]] })} />
          </Row>
          <Row label="Height">
            <NumberField value={stencil.size[1]} step={1} min={1} onChange={(v) => p.setStencil({ image: stencil.image, size: [stencil.size[0], v] })} />
          </Row>
          <Row label="Rotation°">
            <NumberField value={stencil.rotation} step={1} onChange={(v) => p.setStencil({ image: stencil.image, rotation: v })} />
          </Row>
          <Toggle checked={stencil.useColor} label="Use stencil colors" onChange={(v) => p.setStencil({ image: stencil.image, useColor: v })} />
          <p className="muted small">In the viewport: right drag moves, Shift + right drag scales, Ctrl + right drag rotates (Esc cancels).</p>
        </>
      )}
    </>
  );
}

function ToolOptions() {
  const b = usePaint((s) => s.brush);
  const layers = usePaint((s) => s.layers);
  const masked = usePaint((s) => s.masked);
  const p = usePaint.getState();
  return (
    <>
      <Row label="Falloff">
        <SelectField value={b.falloff} options={FALLOFFS} onChange={(v) => p.setBrush({ falloff: v as typeof b.falloff })} />
      </Row>
      <Row label="Spacing" title="Distance between dabs (fraction of the radius)">
        <NumberField value={b.spacing} step={0.01} min={0.01} max={2} onChange={(v) => p.setBrush({ spacing: v })} />
      </Row>
      <Row label="Normal angle" title="Faces turned further than this from the cursor are not painted (180 = paint through)">
        <NumberField value={b.normalAngle} step={1} min={1} max={180} onChange={(v) => p.setBrush({ normalAngle: v })} />
      </Row>
      <Row label="Occlusion" title="Other parts of the model block the brush; off paints through them">
        <Toggle checked={b.occlusion} onChange={(v) => p.setBrush({ occlusion: v })} />
      </Row>
      {b.tool === 'fill' && (
        <>
          <Row label="Fill">
            <SelectField
              value={b.fillMode}
              options={[
                { value: 'island', label: 'Whole UV island' },
                { value: 'flood', label: 'Similar color' },
              ]}
              onChange={(v) => p.setBrush({ fillMode: v as 'island' | 'flood' })}
            />
          </Row>
          {b.fillMode === 'flood' && (
            <Row label="Tolerance">
              <NumberField value={b.fillThreshold} step={0.01} min={0} max={1} onChange={(v) => p.setBrush({ fillThreshold: v })} />
            </Row>
          )}
        </>
      )}
      {b.tool === 'blur' && (
        <Row label="Kernel (px)">
          <NumberField value={b.blurSize} step={1} min={1} max={16} integer onChange={(v) => p.setBrush({ blurSize: v })} />
        </Row>
      )}
      {b.tool === 'clone' && (
        <>
          <Row label="Source">
            <span className="muted small">{b.cloneSource ? 'set (Ctrl+click to move)' : 'Ctrl+click the surface'}</span>
          </Row>
          <Row label="From layer">
            <SelectField
              value={b.cloneLayer === null ? '' : String(b.cloneLayer)}
              options={[{ value: '', label: 'Active layer' }, ...layers.map((l, i) => ({ value: String(i), label: l.name }))]}
              onChange={(v) => p.setBrush({ cloneLayer: v === '' ? null : Number(v) })}
            />
          </Row>
        </>
      )}
      <div className="paint-sep">Mask</div>
      <Toggle checked={b.useMask} label="Masked texels are protected" onChange={(v) => p.setBrush({ useMask: v })} />
      <Toggle checked={b.invertMask} label="Invert mask" onChange={(v) => p.setBrush({ invertMask: v })} />
      <div className="paint-actions">
        <button type="button" className="ghost small" disabled={!masked} onClick={() => p.mask('clear')}>Clear</button>
        <button type="button" className="ghost small" onClick={() => p.mask('fill')}>Mask all</button>
        <button type="button" className="ghost small" onClick={() => p.mask('invert')}>Invert</button>
      </div>
    </>
  );
}

/** Second toolbar row while texture painting. */
export function PaintToolbar() {
  const active = usePaint((s) => s.active);
  const b = usePaint((s) => s.brush);
  const material = usePaint((s) => s.material);
  const modified = usePaint((s) => s.modified);
  const saving = usePaint((s) => s.saving);
  const warning = usePaint((s) => s.warning);
  const path = usePaint((s) => s.path);
  const stencil = usePaint((s) => s.stencil);
  const size = usePaint((s) => s.size);
  useShortcutContext('model.paint', active);
  useCommands(active ? paintCommands() : [], [active]);
  // Sculpt / mesh edit started while painting: save and leave paint mode.
  const otherMode = useModel((s) => s.sculpt.active || s.meshEdit.active);
  useEffect(() => {
    if (active && otherMode) usePaint.getState().end(true);
  }, [active, otherMode]);
  const saveKey = useKeyHint('model.paint.save');
  const swapKey = useKeyHint('model.paint.swap');
  const radiusKey = useKeyHint('model.paint.radius');
  if (!active) return null;
  const p = usePaint.getState();
  const symLabel = ['X', 'Y', 'Z'];
  return (
    <div className="viewport-toolbar mesh-toolbar paint-toolbar" role="toolbar" aria-label="Texture Paint">
      <div className="tb-group segmented" role="group" aria-label="Tools">
        {PAINT_TOOLS.map((t, i) => (
          <button
            key={t.tool}
            type="button"
            className={`tb-btn paint-tool${b.tool === t.tool ? ' active' : ''}`}
            title={`${t.label}: ${t.title} (${i + 1})`}
            aria-pressed={b.tool === t.tool}
            onClick={() => p.setBrush({ tool: t.tool })}
          >
            <Icon name={t.icon as IconName} size={13} />
            <span className="paint-tool-name">{t.label}</span>
          </button>
        ))}
      </div>
      <div className="tb-group">
        <label className="tb-btn paint-slider" title={`Brush radius in screen pixels (${radiusKey || 'F'}, [ and ])`}>
          R
          <input type="range" min={2} max={300} value={Math.min(300, b.radius)} onChange={(e) => p.setBrush({ radius: Number(e.target.value) })} />
          <span className="paint-value">{Math.round(b.radius)}</span>
        </label>
        <label className="tb-btn paint-slider" title="Brush strength (Shift+F)">
          S
          <input type="range" min={0} max={1} step={0.01} value={b.strength} onChange={(e) => p.setBrush({ strength: Number(e.target.value) })} />
          <span className="paint-value">{b.strength.toFixed(2)}</span>
        </label>
        <span className="paint-colors" title="Primary / secondary color">
          <input type="color" value={toHex(b.color)} aria-label="Primary color" onChange={(e) => p.setBrush({ color: fromHex(e.target.value) })} />
          <button type="button" className="ibtn" title={`Swap colors (${swapKey || 'X'})`} onClick={p.swapColors}>
            <Icon name="refresh" size={12} />
          </button>
          <input type="color" value={toHex(b.secondary)} aria-label="Secondary color" onChange={(e) => p.setBrush({ secondary: fromHex(e.target.value) })} />
        </span>
        <button type="button" className="ibtn" title="Sample color under the cursor (S)" onClick={p.sample}>
          <Icon name="pipette" size={14} />
        </button>
        <SelectField value={b.blend} options={BLEND_MODES} onChange={(v) => p.setBrush({ blend: v as typeof b.blend })} />
      </div>
      <div className="tb-group segmented" role="group" aria-label="Symmetry">
        {symLabel.map((axis, k) => (
          <button
            key={axis}
            type="button"
            className={`tb-btn${b.symmetry[k] ? ' active' : ''}`}
            title={`Mirror strokes across the part's local ${axis} axis`}
            aria-pressed={b.symmetry[k]}
            onClick={() => {
              const symmetry = [...b.symmetry] as [boolean, boolean, boolean];
              symmetry[k] = !symmetry[k];
              p.setBrush({ symmetry });
            }}
          >
            {k === 0 && <Icon name="mirror" size={12} />} {axis}
          </button>
        ))}
      </div>
      <Popover label="Brush" title="Falloff, spacing, tool and mask options">
        <ToolOptions />
      </Popover>
      <Popover label="Stencil" title="Screen-space image stencil" active={Boolean(stencil)}>
        <StencilOptions />
      </Popover>
      <Popover label="Bake" title="Bake maps from high-poly parts onto this part" wide>
        {path && <BakePanel low={path} />}
      </Popover>
      <RadialControl />
      <span className="tb-spacer" />
      {warning && (
        <span className="paint-warning" title={warning}>
          <Icon name="warning" size={13} /> UVs
        </span>
      )}
      <span className="muted small mono" title="Material being painted and its texture size">
        {material} · {size[0]}×{size[1]}
      </span>
      <div className="tb-group">
        <button type="button" className="tb-btn" disabled={!modified || saving} title={`Write the layers and the flattened map (${saveKey || 'Ctrl+S'})`} onClick={p.save}>
          <Icon name="check" size={13} /> {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="tb-btn" title="Leave Texture Paint (asks to save)" onClick={() => p.end()}>
          <Icon name="x" size={13} /> Done
        </button>
      </div>
    </div>
  );
}
